import { useState, useEffect, useMemo } from 'react';
import { Button } from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { usePost } from '../../../apis/ApiRequests';
import Header from '../../../components/uiParts/hygiene/Header';
import TitleBar from '../../../components/uiParts/TitleBar';
import {
  Box,
  Card,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from 'react-router-dom';
import { dateToString } from '../../../utils/Library';

// ---------------------------
// date utils
// ---------------------------
function pad2(n) {
	return String(n).padStart(2, "0");
}
function toYmd(date) {
	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
function toYm(date) {
	return `${date.getFullYear()}${pad2(date.getMonth() + 1)}`;
}
function startOfMonth(d) {
	return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
	return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d, diff) {
	return new Date(d.getFullYear(), d.getMonth() + diff, 1);
}

/**
 * monthDays: 月表示用に「前月の埋め」「当月」「翌月の埋め」を含めた 42セル(6週)を返す
 * weekStartsOn: 0=日曜開始, 1=月曜開始
 */
function buildMonthGrid(baseDate, weekStartsOn = 0) {
	const first = startOfMonth(baseDate);

	const firstDow = first.getDay(); // 0..6
	const leading = (firstDow - weekStartsOn + 7) % 7;

	const totalCells = 42;
	const days = [];

	const gridStart = new Date(first);
	gridStart.setDate(first.getDate() - leading);

	for (let i = 0; i < totalCells; i++) {
		const d = new Date(gridStart);
		d.setDate(gridStart.getDate() + i);
		const inMonth = d.getMonth() === baseDate.getMonth();
		const ymd = toYmd(d);
		days.push({ date: d, ymd, inMonth });
	}
	return days;
}

const WEEK_LABELS_SUN = ["日", "月", "火", "水", "木", "金", "土"];

const HygineList = () => {

	const { authInfo, setAuthInfo } = useAuth();

    //===============================================
    // initialize
    //===============================================
    //-----------------------------------------------
    // initialize - セッション情報を取得
    //-----------------------------------------------
	const facilityCode = authInfo.facilityCode;
	const facilityName = authInfo.facilityName;
	const userId = authInfo.userId;
	const userName = authInfo.userName;
	const authority = authInfo.authority;
	const traderCode = authInfo.traderCode;
	const trader = authInfo.trader;
 	const navigate = useNavigate();

	//-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
	// 表示中の月
	const [viewMonth, setViewMonth] = useState(() => {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), 1);
	});

	const monthGrid = useMemo(() => buildMonthGrid(viewMonth, 0), [viewMonth]);
	const todayYmd = toYmd(new Date());
	const headerLabel = useMemo(() => {
		const y = viewMonth.getFullYear();
		const m = viewMonth.getMonth() + 1;
		return `${y}年 ${m}月`;
	}, [viewMonth]);

	const [ monthlyReport, setMonthlyReport ] = useState(null);

    //===============================================
    // API
    //===============================================
	//-----------------------------------------------
	// API - 月のチェック結果集計
	//-----------------------------------------------
	const { response: searchData_res, post: searchData_post } = usePost();
	const searchData = async () => {
        const calendarStartYmd = monthGrid[0]?.ymd;
        const startDate = dateToString(calendarStartYmd);
        const calendarEndYmd = monthGrid[monthGrid.length - 1]?.ymd;
        const endDate = dateToString(calendarEndYmd);
		const postData = {
			'className'    : 'mch.models.table.HygieneList',
			'methodName'   : 'searchData',
			'facilityCode'   : facilityCode,
			'targetMonth' :  toYm(viewMonth),
			'startDate'   : startDate,
			'endDate'   : endDate,
		}
		await searchData_post(postData);
	}
    //===============================================
    // useEffect
    //===============================================
    //-----------------------------------------------
    // useEffect - ロード時の設定
    //-----------------------------------------------
	useEffect(() => {
		searchData();
	}, []);
	//-----------------------------------------------
	// useEffect -月のチェック結果集計
	//-----------------------------------------------
	useEffect(() => {
		if (searchData_res) {
			setMonthlyReport(searchData_res.Data)
		}
	}, [searchData_res]);
	//-----------------------------------------------
	// useEffect -月の変更
	//-----------------------------------------------
	useEffect(() => {
		if (viewMonth) {
			searchData();
		}
	}, [viewMonth]);
    //===============================================
    // eventHandler
    //===============================================
    //-----------------------------------------------
    // eventHandler - 日付の変更
    //-----------------------------------------------
	const handleChange_entryDate = (value) => {};
    //-----------------------------------------------
    // eventHandler - 前月へ移動
    //-----------------------------------------------
	function handlePrev() {
		setViewMonth((d) => addMonths(d, -1));
	}
    //-----------------------------------------------
    // eventHandler - 翌月へ移動
    //-----------------------------------------------
	function handleNext() {
		setViewMonth((d) => addMonths(d, 1));
	}
    //-----------------------------------------------
    // eventHandler - 今日へ移動
    //-----------------------------------------------
	function handleToday() {
		const now = new Date();
		setViewMonth(new Date(now.getFullYear(), now.getMonth(), 1));
	}
    //-----------------------------------------------
    // eventHandler - 高度衛生管理チェック画面へ遷移
    //-----------------------------------------------
	function handleOpenDayCheck(ymd, summary) {
		setAuthInfo(prevState => ({
			...prevState,
			targetDate: ymd,
		}))
        if (!summary || !summary.hygieneId) {
            navigate('/hygieneCheckList?hygieneId=');
        } else {
            navigate('/hygieneCheckList?hygieneId=' + summary.hygieneId);
        }
	}
    //===============================================
    // Function
    //===============================================
    //-----------------------------------------------
	// 日付 → サマリー マップ化
	//-----------------------------------------------
	const monthlyMap = useMemo(() => {
		const map = {};

		if (!Array.isArray(monthlyReport)) {
			return map;
		}

		monthlyReport.forEach(r => {
			map[r.entryDateDsp] = r;
		});

		return map;
	}, [monthlyReport]);
    
    /**
     * 月次サマリーからカレンダー表示記号を決定
     * - データなし ： "－"
     * - 承認済み   ： "◎"
     * - 入力中     ： "△"
     * - 入力済み   ： "〇"
     */
    function getDayMarkFromMonthly(summary) {
        if (!summary) return "－";        // データなし
        if (summary.hygieneStatus == 0) return "△";
        if (summary.hygieneStatus == 1) return "〇";
        if (summary.hygieneStatus == 9) return "◎";
        return "△";
    }
	//===============================================
	// JSXのレンダー
	//===============================================
	return (
		<>
			<Header loginInfo="on" backButton="on" backPage="/hygieneHome" setEntryDate={handleChange_entryDate} />
			<div className='hygineAlertList bodyContens'>
				<TitleBar title="衛生管理カレンダー" color="#d66a35" />

                <Box sx={{ p: 1 }}>
                    {/* ヘッダ */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton onClick={handlePrev} aria-label="prev month">
                                <ChevronLeftIcon />
                            </IconButton>
                            <Typography variant="h7" sx={{ ml: 1, fontWeight: 'bold' }}>
                                {headerLabel}
                            </Typography>
                            <Button
                                color     = "hygiene"
                                variant="contained"
                                sx={{width: '2rem', margin: '0rem',  ml: 20, color: '#fff', backgroundColor: '#d66a35' }}
                                onClick={handleToday}
                                >今日
                            </Button>
                            <IconButton onClick={handleNext} aria-label="next month">
                                <ChevronRightIcon />
                            </IconButton>
                        </Stack>
                        <Box />
                    </Stack>
                    <Card variant="outlined" sx={{ overflow: "hidden" }}>
                        {/* 曜日行 */}
                        <Grid container sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                        {WEEK_LABELS_SUN.map((w) => (
                            <Grid
                            key={w}
                            item
                            xs={12 / 7}
                            sx={{
                                p: 1,
                                bgcolor: "#222B35",
                                color: "#fff",
                                borderRight: "1px solid",
                                borderColor: "#fff",
                                "&:last-of-type": { borderRight: "none" },
                            }}
                            >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {w}
                            </Typography>
                            </Grid>
                        ))}
                        </Grid>

                        {/* 日付セル */}
                        <Grid container>
                        {monthGrid.map(({ ymd, date, inMonth }) => {
                            const day = date.getDate();
                            const isToday = ymd === todayYmd;

                            // const saved = recordsByDay[ymd];
                            // const dayMark = saved ? getDayMark(saved) : null;

                            const summary = monthlyMap[ymd];
                            const dayMark = getDayMarkFromMonthly(summary);
                            return (
                                <Grid
                                    key={ymd}
                                    item
                                    xs={12 / 7}
                                    sx={{
                                        minHeight: 80,
                                        p: 1,
                                        cursor: "pointer",
                                        borderRight: "1px solid",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        "&:nth-of-type(7n)": { borderRight: "none" },
                                        bgcolor: inMonth ? "background.paper" : "action.hover",
                                        position: "relative",
                                    }}
                                    // onClick={() => handleOpenDay(ymd)}
                                    onClick={() => handleOpenDayCheck(ymd, summary)}
                                >
                                    <Stack
                                        direction="column"
                                        alignItems="center"
                                        spacing={0.5}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: isToday ? 800 : 500,
                                                px: isToday ? 1 : 0,
                                                borderRadius: 1,
                                                bgcolor: isToday ? "hygiene.main" : "transparent",
                                                color: isToday ? "primary.contrastText" : "text.primary",
                                                opacity: inMonth ? 1 : 0.6,
                                            }}
                                        >
                                            {day}
                                        </Typography>

                                        {dayMark && (
                                            <Typography
                                                sx={{
                                                    fontWeight: 900,
                                                    fontSize: "1.8rem",
                                                    lineHeight: 1,
                                                    color:
                                                    dayMark === "〇" ? "success.main" :
                                                    dayMark === "×" ? "error.main" :
                                                    dayMark === "△" ? "warning.main" :
                                                    "text.secondary",
                                                }}
                                            >
                                            {dayMark}
                                            </Typography>
                                        )}
                                    </Stack>
                            </Grid>
                            );
                        })}
                        </Grid>
                    </Card>

				</Box>
			</div>
		</>
	);
};

export default HygineList;
